import { noteName, chordName } from './chord.js'

export class ChordPrinter {
    /**
     * @param {import('./midi-device.js').MidiDevice} device
     * @param {object} [options]
     * @param {Console} [options.console]
     * @param {(chord: string) => void} [options.onChordChange]
     * @param {boolean} [options.sharp]
     * @param {boolean} [options.useDegree]
     * @param {number[]} [options.chordChannels]
     * @param {number} [options.scaleKey]
     */
    constructor(device, {
        console = undefined,
        onChordChange = undefined,
        sharp = false,
        useDegree = false,
        scaleKey = 0,
        chordChannels = Array.from({ length: 16 }, (_, i) => i + 1).filter(channel => channel !== 10),
    } = {}) {
        this.device = device
        this.chordChannels = [...chordChannels]
        this.unsubscribe = device.subscribe((kind, channel) => {
            if (channel != null && !this.chordChannels.includes(channel)) return
            if (kind === "noteOn" || (kind === "offset" && device.notes().length > 0)) this.updateChord()
        })
        this.console = console
        this.onChordChange = onChordChange
        /** @type {string | undefined} */
        this.currentChord = undefined
        /** @type {number[]} */
        this.displayedNotes = []
        this.sharp = sharp
        this.useDegree = useDegree
        this.scaleKey = scaleKey
    }
    /**
     * @param {number[]} notes
     * @returns 
     */
    showNotes(notes) {
        return notes
            .map(note => noteName(note, { sharp: this.sharp }))
            .join(" ")
    }
    /**
     * @param {number[]} notes
     * @returns 
     */
    showChord(notes) {
        return chordName(notes, {
            sharp: this.sharp,
            useDegree: this.useDegree,
            scaleKey: this.scaleKey,
        })
    }
    dispose() { this.unsubscribe() }
    /** @param {{sharp: boolean, useDegree: boolean, scaleKey: number, chordChannels?: number[]}} options */
    updateOptions({ sharp, useDegree, scaleKey, chordChannels = this.chordChannels }) {
        const channelsChanged = JSON.stringify(this.chordChannels) !== JSON.stringify(chordChannels)
        if (!channelsChanged && this.sharp === sharp && this.useDegree === useDegree && this.scaleKey === scaleKey) return
        this.sharp = sharp
        this.useDegree = useDegree
        this.scaleKey = scaleKey
        this.chordChannels = [...chordChannels]
        if (channelsChanged) this.updateChord()
        else this.publishChord(this.displayedNotes)
    }
    updateChord() {
        this.publishChord(this.device.reverbNotes(200, this.chordChannels))
    }
    /** @param {number[]} noteNumbers */
    publishChord(noteNumbers) {
        this.displayedNotes = [...noteNumbers]
        const notes = this.showNotes(noteNumbers)
        const chord = this.showChord(noteNumbers)
        if (this.console && typeof this.console.log === "function") {
            this.console.log(`${notes} = ${chord}`)
        } else if (this.console === undefined) {
            console.log(`${notes} = ${chord}`)
        }
        if (this.currentChord !== chord) {
            if (typeof this.onChordChange === "function") {
                this.onChordChange(chord)
            }
        }
        this.currentChord = chord
    }
}
